import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  FaExclamationCircle,
  FaTimesCircle,
  FaCheckCircle,
  FaArrowLeft,
  FaLock,
  FaRedo,
} from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { GrPowerCycle } from "react-icons/gr";
import axiosInstance from "../../../AxiosInstance";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { RotatingLines } from "react-loader-spinner";
import config from "../../../config/config";

const MyPaymentsContent = () => {
  const navigate = useNavigate();
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [filterWorkerName, setFilterWorkerName] = useState("");
  const [filterPaymentId, setFilterPaymentId] = useState("");
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");

  const tableContainerRef = useRef(null);
  const tableRowRef = useRef(null);

  const baseURL = config.API_BASE_URL;

  const getQueryParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const pathParts = window.location.pathname.split("/");
    const paymentIntentIdFromPath = pathParts[pathParts.length - 1];
    return { paymentIntentId: paymentIntentIdFromPath };
  };

  useEffect(() => {
    const { paymentIntentId } = getQueryParams();
    setPaymentIntentId(paymentIntentId);
  }, []);

  useEffect(() => {
    const fetchPayments = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(`${baseURL}/v1/osh-payments`);
        let payments = response.data;
        console.log("Fetched payments:", payments);

        payments = payments.sort(
          (a, b) => new Date(b.created * 1000) - new Date(a.created * 1000)
        );

        setAllPayments(payments);
        setFilteredPayments(payments);

        if (paymentIntentId) {
          console.log("paymentIntentId from URL:", paymentIntentId);
          const selectedPayment = payments.find(
            (payment) => payment.paymentIntentId === paymentIntentId
          );
          if (selectedPayment) {
            console.log("Selected payment found:", selectedPayment);
            setSelectedPayment(selectedPayment);
            setIsModalOpen(true);
          } else {
            console.log("Selected payment not found");
          }
        }
      } catch (error) {
        console.error("Error fetching payments:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        console.log("Tab is now visible, fetching data...");
        try {
          await fetchPayments();
          console.log("Fetching payments...");
        } catch (error) {
          console.error("Error fetching payments on visibility change:", error);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [baseURL, paymentIntentId]);

  useEffect(() => {
    filterPayments();
  }, [
    filterWorkerName,
    filterPaymentId,
    filterStartDate,
    filterEndDate,
    filterStatus,
    allPayments,
  ]);

  useEffect(() => {
    const adjustItemsPerPage = () => {
      if (tableContainerRef.current && tableRowRef.current) {
        const containerHeight = tableContainerRef.current.offsetHeight;
        const rowHeight = tableRowRef.current.offsetHeight;

        let headerHeight = document.querySelector(
          ".supportclaim-header"
        )?.offsetHeight;
        headerHeight += rowHeight;

        const paginationHeight = document.querySelector(
          ".supportclaim-pagination-controls"
        )?.offsetHeight;

        const availableHeight =
          containerHeight - headerHeight - paginationHeight - rowHeight * 3;
        const visibleRows = Math.floor(availableHeight / rowHeight);

        setItemsPerPage(visibleRows || 5);
      }
    };

    adjustItemsPerPage();

    window.addEventListener("resize", adjustItemsPerPage);

    return () => {
      window.removeEventListener("resize", adjustItemsPerPage);
    };
  }, [allPayments]);

  const generateDropdownOptions = () => {
    let base = itemsPerPage;
    let secondOption = Math.ceil((base * 2) / 5) * 5;
    return [base, secondOption, secondOption * 2].filter(
      (value, index, self) => self.indexOf(value) === index
    );
  };

  const dropdownOptions = generateDropdownOptions();

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  useEffect(() => {
    if (paymentIntentId && allPayments.length > 0) {
      console.log("Checking paymentIntentId on effect:", paymentIntentId);
      const selectedPayment = allPayments.find(
        (payment) => payment.paymentIntentId === paymentIntentId
      );
      if (selectedPayment) {
        console.log("Selected payment found:", selectedPayment);
        setSelectedPayment(selectedPayment);
        setIsModalOpen(true);
      } else {
        console.log("Selected payment not found");
      }
    }
  }, [paymentIntentId, allPayments]);

  const lastItemIndex = currentPage * itemsPerPage;
  const firstItemIndex = lastItemIndex - itemsPerPage;
  const currentItems = filteredPayments.slice(firstItemIndex, lastItemIndex);
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);

  const handleModalClose = () => {
    setShowModal(false);
    navigate(`/account/admin-dashboard/billing-invoicing/payments`);
  };

  const handleBackClick = () => {
    setIsModalOpen(false);
    navigate(`/account/admin-dashboard/billing-invoicing/payments`);
  };

  const handleFilterChange = (filterSetter) => (e) => {
    filterSetter(e.target.value);
  };

  const filterPayments = () => {
    const filtered = allPayments.filter((payment) => {
      const paymentDate = new Date(payment.created * 1000);
      const endDate = new Date(filterEndDate);
      endDate.setHours(23, 59, 59, 999);
      const workerName = `${payment.workerFirstName || ""} ${
        payment.workerLastName || ""
      }`.toLowerCase();
      return (
        (filterWorkerName === "" ||
          workerName.includes(filterWorkerName.toLowerCase())) &&
        (filterPaymentId === "" ||
          payment.paymentIntentId.includes(filterPaymentId)) &&
        (filterStartDate === null || paymentDate >= filterStartDate) &&
        (filterEndDate === null || paymentDate <= endDate) &&
        (filterStatus === "" || payment.status === filterStatus)
      );
    });

    setFilteredPayments(filtered);
  };

  const handleClearFilters = () => {
    setFilterWorkerName("");
    setFilterPaymentId("");
    setFilterStartDate(null);
    setFilterEndDate(null);
    setFilterStatus("");
  };

  const handleViewMore = (paymentIntentId) => {
    setSelectedPayment(
      filteredPayments.find(
        (payment) => payment.paymentIntentId === paymentIntentId
      )
    );
    setIsModalOpen(true);
    navigate(
      `/account/admin-dashboard/billing-invoicing/payments/details/${paymentIntentId}`
    );
  };

  return (
    <div>
      <div
        ref={tableContainerRef}
        className="adminsupportclaim-support-container"
      >
        {showModal && (
          <div className="success-message-container">
            <div className="claim-success-message">
              <FaCheckCircle className="invoice-check-icon" />
              <h4>{successMessage}</h4>
              <IoMdClose
                className="success-message-close"
                onClick={handleModalClose}
              />
            </div>
          </div>
        )}
        <div className="supportclaim-header"></div>
        {!isModalOpen && (
          <div className="invoice-filter-container">
            <div className="invoice-filter-itemwn">
              <label htmlFor="name-filter">Filter by worker name</label>
              <input
                id="name-filter"
                type="text"
                placeholder="Payment description ..."
                value={filterWorkerName}
                onChange={handleFilterChange(setFilterWorkerName)}
              />
            </div>
            <div className="invoice-filter-itemii">
              <label htmlFor="payment-id-filter">Filter by Payment ID</label>
              <input
                id="payment-id-filter"
                type="text"
                placeholder="Payment ID ..."
                value={filterPaymentId}
                onChange={handleFilterChange(setFilterPaymentId)}
              />
            </div>
            <div className="invoice-filter-itemds">
              <label>Filter by Payment date range</label>
              <div className="invoice-date-selects">
                <DatePicker
                  selected={filterStartDate}
                  onChange={(date) => setFilterStartDate(date)}
                  selectsStart
                  startDate={filterStartDate}
                  endDate={filterEndDate}
                  placeholderText="Start date"
                />
                <DatePicker
                  selected={filterEndDate}
                  onChange={(date) => setFilterEndDate(date)}
                  selectsEnd
                  startDate={filterStartDate}
                  endDate={filterEndDate}
                  placeholderText="End date"
                  minDate={filterStartDate}
                />
              </div>
            </div>
            <div className="invoice-filter-itemst">
              <label htmlFor="status-filter">Filter by status</label>
              <select
                id="status-filter"
                value={filterStatus}
                onChange={handleFilterChange(setFilterStatus)}
              >
                <option value="">All Statuses</option>
                <option value="succeeded">Succeeded</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div className="clear-filters-container">
              <button
                className="clear-filters-button"
                onClick={handleClearFilters}
              >
                <FaRedo className="invoice-filter-reset" /> Reset
              </button>
            </div>
          </div>
        )}
        {loading ? (
          <div className="loading-spinner">
            <RotatingLines
              visible={true}
              height="96"
              width="56"
              color="grey"
              strokeWidth="3"
              animationDuration="0.75"
              ariaLabel="rotating-lines-loading"
              wrapperStyle={{}}
              wrapperClass=""
            />
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="no-claims-message">
            <p>No matching Payments found ...</p>
          </div>
        ) : (
          <>
            <table
              className={`supportclaim-support-table ${
                isModalOpen ? "hidden" : ""
              }`}
            >
              <thead>
                <tr>
                  <th>Payment ID</th>
                  <th>Receiver Account</th>
                  <th>Date</th>
                  <th>Worker</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>View More</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((item, index) => (
                  <tr
                    key={item.paymentIntentId || index}
                    ref={index === 0 ? tableRowRef : null}
                  >
                    <td className="invoice-idtd-style">
                      <span className="invoice-id-style">
                        {item.paymentIntentId}
                      </span>
                    </td>
                    <td>{item.transferDestination}</td>
                    <td>
                      {new Date(item.created * 1000).toLocaleDateString()}
                    </td>
                    <td>{`${item.workerFirstName} ${item.workerLastName}`}</td>
                    <td>
                      <strong>${(item.amount / 100).toFixed(2)}</strong>
                    </td>
                    <td>
                      {item.status === "succeeded" ? (
                        <>
                          Succeeded
                          <FaCheckCircle className="claim-status-completed" />
                        </>
                      ) : item.status === "failed" ? (
                        <>
                          Failed
                          <FaTimesCircle className="claim-status-void" />
                        </>
                      ) : item.status === "pending" ? (
                        <>
                          Pending
                          <GrPowerCycle className="claim-status-open" />
                        </>
                      ) : (
                        item.status
                      )}
                    </td>
                    <td className="invoice-sent-style">
                      <button
                        className="view-more-button"
                        onClick={() => handleViewMore(item.paymentIntentId)}
                      >
                        View more
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="items-per-page-selector">
              <label htmlFor="items-per-page">Show items per page</label>
              <select
                id="items-per-page"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
              >
                {dropdownOptions.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
            {isModalOpen && selectedPayment && (
              <div className="claim-details open">
                <div
                  className="claim-back-containeradmn"
                  onClick={handleBackClick}
                >
                  <FaArrowLeft className="claim-back-icon" /> Back to Payments
                </div>
                <table>
                  <tbody>
                    <tr>
                      <td>
                        <strong>Payment Intent ID</strong>
                      </td>
                      <td>
                        <strong>{selectedPayment.paymentIntentId}</strong>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Status</strong>
                      </td>
                      <td className="invoice-details-status">
                        {selectedPayment.status}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Date</strong>
                      </td>
                      <td className="td-date">
                        {new Date(
                          selectedPayment.created * 1000
                        ).toLocaleDateString()}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Estimated Transfer Amount</strong>
                      </td>
                      <td className="claim-total-amount">
                        $
                        {(
                          selectedPayment.estimatedTransferAmount / 100
                        ).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Capture Method</strong>
                      </td>
                      <td>
                        <strong>
                          {selectedPayment.captureMethod
                            ? selectedPayment.captureMethod
                                .charAt(0)
                                .toUpperCase() +
                              selectedPayment.captureMethod.slice(1)
                            : ""}
                        </strong>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Currency</strong>
                      </td>
                      <td>
                        <strong>
                          {selectedPayment.currency
                            ? selectedPayment.currency.toUpperCase()
                            : ""}
                        </strong>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Charged OSH Fee</strong>
                      </td>
                      <td className="claim-total-amount">
                        ${(selectedPayment.chargedOshFee / 100).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Customer ID</strong>
                      </td>
                      <td>{selectedPayment.customer}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Client Name</strong>
                      </td>
                      <td>
                        <strong>{`${selectedPayment.clientFirstName} ${selectedPayment.clientLastName}`}</strong>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Invoice ID</strong>
                      </td>
                      <td>
                        <a
                          href={`/account/admin-dashboard/billing-invoicing/invoices/details/${selectedPayment.invoice}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="agreement-link"
                        >
                          {selectedPayment.invoice}
                        </a>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Receiver Account</strong>
                      </td>
                      <td>{selectedPayment.transferDestination}</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Worker Name</strong>
                      </td>
                      <td>
                        <strong>{`${selectedPayment.workerFirstName} ${selectedPayment.workerLastName}`}</strong>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Stripe Processing Fee</strong>
                      </td>
                      <td className="claim-total-amount">
                        $
                        {(selectedPayment.stripeProcessingFee / 100).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Billed Amount</strong>
                      </td>
                      <td className="claim-total-amount">
                        ${(selectedPayment.amount / 100).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Transfer ID</strong>
                      </td>
                      <td>{selectedPayment.transfer}</td>
                    </tr>
                  </tbody>
                </table>
                <div className="edit-button-cell">
                  <span className="claim-button-container">
                    <button
                      className="claim-approve-button"
                      onClick={handleBackClick}
                    >
                      Close
                    </button>
                  </span>
                </div>
              </div>
            )}

            {!isModalOpen && (
              <div className="supportclaim-pagination-controls">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  <img src="/images/back-icon.png" alt="Previous" /> Previous
                </button>

                <span>
                  {currentPage} of {totalPages}
                </span>

                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next <img src="/images/next-icon.png" alt="Next" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyPaymentsContent;
